import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  Box,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { PhotoCamera, AutoFixHigh } from "@mui/icons-material";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { FamilyMember } from "../utils/familyTreeUtils";
import { useAuth, db, storage } from "../firebase";

interface PersonDetailCardProps {
  member: FamilyMember;
  onChange: (updatedMember: FamilyMember) => Promise<void>;
  /** e.g. "Paternal Grandmother" — tells the user who they're editing */
  relationshipLabel?: string;
  /** e.g. "Father of John" — extra orientation for deeper generations */
  parentOfLabel?: string;
}

const PersonDetailCard: React.FC<PersonDetailCardProps> = ({
  member,
  onChange,
  relationshipLabel,
  parentOfLabel,
}) => {
  const { user } = useAuth();
  const userId = user?.uid;

  const [name, setName] = useState(member?.name || "");
  const [birthdate, setBirthdate] = useState(member?.birthdate || "");
  const [deathdate, setDeathdate] = useState(member?.deathdate || "");
  const [image, setImage] = useState(member?.imageMetadata || "");
  const [isEdited, setIsEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEnhancementUpsell, setShowEnhancementUpsell] = useState(false);
  const [showEnhancementDialog, setShowEnhancementDialog] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");

  useEffect(() => {
    setName(member?.name || "");
    setBirthdate(member?.birthdate || "");
    setDeathdate(member?.deathdate || "");
    setImage(member?.imageMetadata || "");
    setIsEdited(false);

    // Show enhancement upsell if there's a photo and it hasn't been enhanced
    const hasPhoto =
      member?.imageMetadata &&
      !member.imageMetadata.includes("Placeholder") &&
      member.imageMetadata.startsWith("https://");
    const hasBeenEnhanced = member?.enhancementStatus === "enhanced";
    setShowEnhancementUpsell(Boolean(hasPhoto && !hasBeenEnhanced));
  }, [member]);

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => {
    setter(value);
    setIsEdited(true);
  };

  const handleSave = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Call the onChange callback to let the parent component handle the Firestore update
      // This ensures the array structure is maintained
      setIsEdited(false);
      await onChange({
        ...member,
        name,
        birthdate,
        deathdate,
        imageMetadata: image,
      });
    } catch (error) {
      console.error("Error saving member: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    memberId: string
  ) => {
    if (!userId) return;
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const storageRef = ref(storage, `users/${userId}/images/${memberId}.jpg`);
      setLoading(true);
      try {
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        setImage(downloadURL);
        setUploadedImageUrl(downloadURL);
        // Don't set isEdited=true for image uploads - they should save immediately

        // Image updates will be handled by the parent component via onChange
        await onChange({
          ...member,
          name,
          birthdate,
          deathdate,
          imageMetadata: downloadURL,
        });

        // Small delay to ensure Firestore write completes
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Show enhancement dialog after successful upload (if not already enhanced)
        if (!member?.enhancementStatus || member.enhancementStatus === "none") {
          console.log(`📸 Opening enhancement dialog for member:`, member.id);
          setShowEnhancementDialog(true);
        }
      } catch (error) {
        console.error("Error uploading image: ", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEnhancementRequest = async () => {
    if (!userId || !member) return;

    try {
      console.log(`✨ Enhancement requested for member:`, member.id);
      // Update the member with enhancement request
      await onChange({
        ...member,
        name,
        birthdate,
        deathdate,
        imageMetadata: image,
        enhancementStatus: "requested",
        enhancementRequestedAt: new Date().toISOString(),
      });

      setShowEnhancementUpsell(false);

      // Here you could also trigger a payment flow or add to cart
      // For now, we'll just mark it as requested
    } catch (error) {
      console.error("Error requesting photo enhancement:", error);
    }
  };

  if (!member) return null;

  // Create a dynamic title based on the person's name or a default
  const cardTitle = name || member.name || "Family Member Details";

  return (
    <Box>
      <Card
        elevation={5}
        sx={{ maxWidth: 800, overflow: "hidden", padding: 0 }}
      >
        {/* Custom Header Banner - Full Width */}
        <Box
          sx={{
            backgroundColor: "#8B4A6B", // Maroon color
            color: "white",
            padding: "20px 24px",
            margin: 0,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            fontWeight="bold"
            color="white"
            sx={{ margin: 0, marginBottom: "4px" }}
          >
            {relationshipLabel ? `${relationshipLabel}` : cardTitle}
            {relationshipLabel && name && (
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: "normal", opacity: 0.85, ml: 1 }}
              >
                — {name}
              </Typography>
            )}
          </Typography>
          <Typography
            variant="body2"
            color="white"
            sx={{ opacity: 0.9, margin: 0 }}
          >
            {parentOfLabel
              ? `Parent of ${parentOfLabel} — edit their information and photo`
              : "Edit family member information and upload photos"}
          </Typography>
        </Box>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "center", sm: "flex-start" },
          }}
        >
          {/* Avatar and upload */}
          <Box
            sx={{
              marginRight: { xs: 0, sm: 2 },
              marginBottom: { xs: 2, sm: 0 },
              textAlign: "center",
            }}
          >
            <Avatar
              src={image}
              sx={{
                width: { xs: 140, sm: 180, md: 220 },
                height: { xs: 140, sm: 180, md: 220 },
              }}
            >
              {loading && <CircularProgress />}
            </Avatar>
            <input
              accept="image/*"
              id="icon-button-file"
              type="file"
              hidden
              onChange={(e) => handleImageChange(e, member.id)}
            />
            <label htmlFor="icon-button-file">
              <IconButton component="span" disabled={loading}>
                <PhotoCamera />
              </IconButton>
            </label>
          </Box>
          {/* Text fields */}
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => handleFieldChange(setName, e.target.value)}
              fullWidth
              margin="dense"
              disabled={loading}
            />
            <TextField
              label="Birthdate"
              type="date"
              value={birthdate}
              onChange={(e) => handleFieldChange(setBirthdate, e.target.value)}
              fullWidth
              margin="dense"
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Death Date (leave empty if living)"
              type="date"
              value={deathdate}
              onChange={(e) => handleFieldChange(setDeathdate, e.target.value)}
              fullWidth
              margin="dense"
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </CardContent>
        <CardActions>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isEdited || loading}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
                cursor: "not-allowed",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
          {showEnhancementUpsell && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoFixHigh />}
              color="warning"
              onClick={handleEnhancementRequest}
              sx={{ mt: 1 }}
            >
              Enhance Photo
            </Button>
          )}
          {member?.enhancementStatus === "requested" && (
            <Chip
              label="Enhancement Requested"
              color="info"
              size="small"
              sx={{ marginLeft: 1 }}
            />
          )}
          {member?.enhancementStatus === "enhanced" && (
            <Chip
              label="Enhanced"
              color="success"
              size="small"
              sx={{ marginLeft: 1 }}
            />
          )}
        </CardActions>
      </Card>

      {/* Photo Enhancement Dialog */}
      <Dialog
        open={showEnhancementDialog}
        onClose={() => {
          console.log(`🚫 Enhancement dialog closed via onClose`);
          setShowEnhancementDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AutoFixHigh color="primary" />
            <Typography variant="h6">Enhance This Photo?</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1">
              Great photo! Would you like to enhance it with our AI-powered
              photo restoration?
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <Box flex={1}>
                <img
                  src={uploadedImageUrl}
                  alt="Uploaded photo"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                />
                <Typography
                  variant="caption"
                  display="block"
                  textAlign="center"
                  mt={1}
                >
                  Original Photo
                </Typography>
              </Box>
              <Box flex={1} textAlign="center">
                <Typography variant="body2" color="textSecondary" mb={1}>
                  AI Enhancement includes:
                </Typography>
                <Typography
                  variant="body2"
                  component="ul"
                  sx={{ textAlign: "left", pl: 2 }}
                >
                  <li>Color restoration</li>
                  <li>Noise reduction</li>
                  <li>Sharpness improvement</li>
                  <li>Damage repair</li>
                </Typography>
                <Chip
                  label="$5 per photo"
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                  mt={1}
                >
                  💡 Best value: Order bulk enhancement during checkout for 20%
                  savings!
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              console.log(`❌ Enhancement dialog closed - Maybe Later`);
              setShowEnhancementDialog(false);
            }}
          >
            Maybe Later
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              console.log(`✅ Enhancement dialog - Enhance Photo clicked`);
              handleEnhancementRequest();
              setShowEnhancementDialog(false);
            }}
            startIcon={<AutoFixHigh />}
          >
            Enhance Photo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonDetailCard;
